import { QueryStatus, skipToken } from "@reduxjs/toolkit/query/react";
import { api } from "../../state/api";
import {
  type PlaybookContentFaq,
  type PlaybookContentFaqPreview,
  type PlaybookContentFaqSearchResult,
  PlaybookContentIntegrationType,
  PlaybookContentPlanType,
  type PlaybookContentSearchResponse,
} from "../../state/api.types";
import { playbook } from "../../state/playbook";
import { useTypedDispatch, useTypedSelector } from "../../state/store";
import { filterPlaybookGuides } from "../../utilities/playbook";

export function usePlaybookFilters() {
  const dispatch = useTypedDispatch();
  const planFilter = useTypedSelector((s) => s.playbook.planFilter);
  const integrationFilter = useTypedSelector(
    (s) => s.playbook.integrationFilter,
  );

  const setPlanType = (value: PlaybookContentPlanType | null) => {
    dispatch(playbook.actions.setPlanFilter(value));
  };

  const setIntegrationType = (value: PlaybookContentIntegrationType) => {
    dispatch(playbook.actions.setIntegrationFilter(value));
  };

  return { planFilter, integrationFilter, setPlanType, setIntegrationType };
}

export function useFilteredPlaybookContentOverview() {
  const { planFilter, integrationFilter } = usePlaybookFilters();
  const query = api.useGetPlaybookContentOverviewQuery(null);

  if (!query.data) return query;

  const guides = filterPlaybookGuides(
    query.data.guides,
    planFilter,
    integrationFilter,
  );

  return {
    ...query,
    data: {
      ...query.data,
      guides,
    },
  };
}

/**
 * Given an id or slug, provides a playbook guide and its section. Falls back
 * to overview data if available while fetching the full guide.
 */
export function usePlaybookGuide(idOrSlug: string | null | undefined) {
  const { currentData: data, isLoading } = api.useGetPlaybookContentGuideQuery(
    idOrSlug ?? skipToken,
  );
  const overviewData = useTypedSelector(
    api.endpoints.getPlaybookContentOverview.select(null),
  ).data;

  const guide =
    data?.guide ??
    overviewData?.guides.find(
      (g) => g.short_id === idOrSlug || g.slug === idOrSlug,
    );
  const section = data?.guide
    ? data.guide.section
    : overviewData?.sections.find((s) => s.slug === guide?.section);

  return { guide, section, isLoading };
}

/**
 * Given a slug, provides a playbook FAQ. Falls back to search results if available
 * while fetching the full FAQ.
 */
export function usePlaybookFAQ(idOrSlug: string | null | undefined): {
  faq: PlaybookContentFaq | PlaybookContentFaqPreview | undefined;
  isLoading: boolean;
} {
  const { currentData: data, isLoading } = api.useGetPlaybookContentFaqQuery(
    idOrSlug ?? skipToken,
  );
  const searchData = useTypedSelector((state) => {
    const entries = Object.values(state.api.queries).filter(
      (q) =>
        q?.endpointName === "getPlaybookContentSearch" &&
        q.status === QueryStatus.fulfilled,
    );
    const latest = entries.reduce<(typeof entries)[0]>(
      (a, b) =>
        (a?.fulfilledTimeStamp ?? 0) >= (b?.fulfilledTimeStamp ?? 0) ? a : b,
      undefined,
    );
    return latest?.data as PlaybookContentSearchResponse | undefined;
  });

  const faq =
    data?.faq ??
    searchData?.results
      .filter((r): r is PlaybookContentFaqSearchResult => r.type === "faq")
      .find((r) => r.faq.short_id === idOrSlug || r.faq.slug === idOrSlug)?.faq;

  return { faq, isLoading };
}

/**
 * Given a section slug, provides the guides within that section.
 */
export function useSectionGuides(slug: string | null | undefined) {
  const { data, isLoading } = api.useGetPlaybookContentOverviewQuery(null);

  const guides = data?.guides.filter((g) => g.section === slug) ?? [];

  return { guides, isLoading };
}
